<template>
  <el-drawer
    :direction="direction"
    :with-header="false"
    :modal-append-to-body="false"
    class="sidebar__sub-merchants-drawer__wrapper"
    custom-class="sidebar__sub-merchants-drawer"
    :visible="visible"
    v-on="$listeners"
  >
    <div>
      <div class="sidebar__sub-merchants">
        <div class="sidebar__sub-merchants-head">
          <div class="sidebar__sub-merchants-head-search">
            <el-input
              :value="subMerchantsSearchText"
              clearable
              class="search-input"
              :placeholder="$t('sidebar_search_placeholder')"
              @input="handleInputMerchantSearchBox"
            >
              <span slot="prefix" class="sidebar__search-svg-container">
                <svg-icon icon-class="search" />
              </span>
            </el-input>
          </div>
          <div class="sidebar__sub-merchants-head-text">
            {{ subMerchantAccounts.length }}
            {{ $t('sidebar_accounts_found') }}
          </div>
        </div>
        <div
          ref="merchantAccountList"
          class="sidebar__sub-merchants-content"
          :class="{ 'is-scrollable': isScrollable }"
        >
          <div v-if="searchNotFound" class="sidebar__sub-merchants-no-results">
            {{ $t('sidebar_no_results') }}
          </div>
          <div
            v-for="item in subMerchantAccounts"
            :key="item.merchantId"
            class="sidebar__sub-merchants-item"
            :class="{
              'is-active': item.merchantId === selectedMerchant,
            }"
            @click="handleSelectSubMerchant(item.merchantId)"
          >
            <div class="sidebar__sub-merchants-item__svg-container">
              <svg-icon icon-class="store-solid" />
            </div>
            <div class="sidebar__sub-merchants-item__name-container">
              <el-tooltip
                placement="right"
                :open-delay="200"
                :content="getLocalizedSubMerchantName(item)"
                :z-index="2201"
              >
                <div class="sidebar__sub-merchants-item-name ellipsis">
                  {{ getLocalizedSubMerchantName(item) }}
                </div>
              </el-tooltip>
            </div>
            <div v-if="item.merchantId === selectedMerchant">
              <svg-icon
                icon-class="check-filled"
                width="24px"
                height="24px"
                color="#F2F2F2"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import { getLocalizedValue } from '@/utils/i18n'

export default {
  name: 'SidebarMerchantAccountList',
  props: {
    direction: {
      type: String,
      required: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    selectedMerchant: {
      type: String,
      required: true,
    },
    subMerchants: {
      type: Array,
      required: true,
    },
    subMerchantAccounts: {
      type: Array,
      required: true,
    },
    subMerchantsSearchText: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isScrollable: false,
    }
  },
  computed: {
    searchNotFound() {
      return (
        this.subMerchantsSearchText && this.subMerchantAccounts.length === 0
      )
    },
  },
  mounted() {
    this.calculateContentHeight()
  },
  updated() {
    this.calculateContentHeight()
  },
  methods: {
    calculateContentHeight() {
      if (!this.$refs.merchantAccountList) {
        return
      }

      const { scrollHeight, clientHeight } = this.$refs.merchantAccountList

      this.isScrollable = scrollHeight > clientHeight
    },
    handleSelectSubMerchant(merchantId) {
      this.$emit('select', merchantId)
    },
    getLocalizedSubMerchantName(item) {
      return getLocalizedValue(item.merchantName, item.arMerchantName)
    },
    handleInputMerchantSearchBox(value) {
      this.$emit('update:subMerchantsSearchText', value)
    },
  },
}
</script>

<style lang="scss" scoped>
.sidebar__sub-merchants-drawer__wrapper {
  --background-color: #8267db;
  --scollbar-color: #5e47b7;
  --sub-merchants-item-height: 24px;
  --sub-merchants-item-space-between: 24px;
  --sub-merchants-item-name-container-with: 162px;
  --sub-merchants-item-svg-store-color: #ccc;

  left: calc(var(--sidebar-width) - 1px);
  background-color: rgb(0 0 0 / 50%);

  ::v-deep {
    .sidebar__sub-merchants-drawer {
      top: var(--header-height);
      width: var(--sidebar-drawer-width) !important;
      padding: 0;
      background-color: var(--background-color);
      border-top-right-radius: 12px;

      .sidebar__sub-merchants-head {
        padding: 20px 20px 20px 21px;

        &-search {
          position: relative;

          .search-input {
            .el-input__inner {
              height: 44px;
              padding: 10px 14px 10px 44px;
              font-family: var(--font-family--secondary);
              font-size: 11px;
              font-weight: 400;
              line-height: 13.2px;
              text-align: start;
              border-radius: 62px;

              &:hover,
              &:focus,
              &:active {
                border-color: #999;
              }

              [dir='rtl'] & {
                padding: 10px 44px 10px 14px;
              }
            }

            .el-input__prefix {
              left: 14px;
              display: flex;
              align-items: center;

              [dir='rtl'] & {
                right: 14px;
                left: unset;
              }
            }

            .el-input__suffix {
              right: 14px;
              font-size: 16px;

              [dir='rtl'] & {
                right: unset;
                left: 14px;
              }
            }
          }

          .sidebar__search-svg-container {
            font-size: 24px;
            color: #999;
          }
        }

        &-text {
          margin-top: 24px;
          font-size: 11px;
          font-weight: 400;
          line-height: 16px;
          color: #e5e5e5;
          text-align: start;
        }
      }

      .sidebar__sub-merchants-content {
        height: calc(100vh - var(--header-height) - 108px);
        padding: 4px 20px 20px 20px;
        margin-top: 0;
        overflow-y: auto;
        scrollbar-color: var(--scollbar-color) var(--background-color);

        &.is-scrollable {
          padding-inline-end: 8px;

          .sidebar__sub-merchants-item.is-active {
            --sub-merchants-item-name-container-with: 149px;
          }
        }

        .sidebar__sub-merchants-no-results {
          color: #fff;
        }

        [dir='rtl'] & {
          padding: 4px 20px 20px 0;
        }
      }

      .sidebar__sub-merchants-item {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        height: var(--sub-merchants-item-height);
        margin-bottom: var(--sub-merchants-item-space-between);
        font-size: 14px;
        font-weight: 400;
        line-height: 24px;
        color: #e5e5e5;
        text-align: start;

        > * {
          flex: 0 0 24px;
        }

        &.is-active {
          --sub-merchants-item-name-container-with: 149px;
          --sub-merchants-item-svg-store-color: #fff;

          font-weight: 600;
          color: #fff;
        }

        &__svg-container {
          font-size: 24px;
          color: var(--sub-merchants-item-svg-store-color);
        }

        &__name-container {
          flex: 1 1 auto;
          max-width: var(--sub-merchants-item-name-container-with);
          margin-inline-start: 6px;

          .ellipsis {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        &:hover {
          --sub-merchants-item-svg-store-color: #fff;

          color: #fff;
        }
      }

      [dir='rtl'] & {
        border-top-left-radius: 12px;
        border-top-right-radius: 0;
      }
    }
  }

  [dir='rtl'] & {
    right: calc(var(--sidebar-width) - 1px);
    left: 0;
  }
}
</style>
